declare global {
	interface Window {
		Gleap: {
			initialize: (apiKey: string) => void;
			showFeedbackButton: (show: boolean) => void;
			setEnvironment: (env: "dev" | "staging" | "prod") => void;
			isOpened: () => boolean;
			open: () => void;
			close: () => void;
			clearIdentity: () => void;
			identify: (userId: string, userData: GleapUserData) => void;
			updateContact: (contactData: GleapUserData) => void;
			startClassicForm: (formType: string, screenshot?: boolean) => void;
			playSound: (enabled: boolean) => void;
			disableConsoleLogOverwrite: () => void;
		};
		__gleapInitialized?: boolean;
		__gleapQueue?: Array<() => void>;
	}
}

export interface GleapUserData {
	name?: string;
	email?: string;
	phone?: string;
	value?: number;
	plan?: string;
	companyId?: string;
	companyName?: string;
	createdAt?: Date;
	customData?: Record<string, unknown>;
}

// Helper function to check if we're in a browser and Gleap is available
function isGleapAvailable(): boolean {
	return typeof window !== "undefined" && !!window.Gleap;
}

// Helper function to safely execute Gleap functions or queue them for later
function safelyExecuteGleapFunction(fn: () => void): void {
	if (typeof window === "undefined") return;

	if (window.__gleapInitialized) {
		fn();
	} else {
		// Initialize the queue if it doesn't exist
		if (!window.__gleapQueue) {
			window.__gleapQueue = [];
		}

		// Add the function to the queue
		window.__gleapQueue.push(fn);
	}
}

// Process the queue of pending Gleap operations
function processGleapQueue(): void {
	if (typeof window === "undefined" || !window.__gleapQueue) return;

	while (window.__gleapQueue.length > 0) {
		const fn = window.__gleapQueue.shift();
		if (fn) fn();
	}
}

export function mapVercelEnvToGleapEnv(
	vercelEnv?: string,
): "dev" | "staging" | "prod" {
	switch (vercelEnv) {
		case "production":
			return "prod";
		case "preview":
			return "staging";
		default:
			return "dev";
	}
}

export function initializeGleap(apiKey?: string, env?: string): void {
	// Only initialize if we're in the browser
	if (typeof window === "undefined") return;
	if (!apiKey) return;

	const script = `
	!function(Gleap,t,i){if(!(Gleap=window.Gleap=window.Gleap||[]).invoked){for(window.GleapActions=[],Gleap.invoked=!0,Gleap.methods=["identify","setEnvironment","setTags","attachCustomData","setCustomData","removeCustomData","clearCustomData","registerCustomAction","trackEvent","log","preFillForm","showSurvey","sendSilentCrashReport","startFeedbackFlow","startBot","setAppBuildNumber","setAppVersionCode","setApiUrl","setFrameUrl","isOpened","open","close","on","setLanguage","setOfflineMode","initialize","disableConsoleLogOverwrite","logEvent","hide","enableShortcuts","showFeedbackButton","destroy","getIdentity","isUserIdentified","clearIdentity","openConversations","openConversation","openHelpCenterCollection","openHelpCenterArticle","openHelpCenter","searchHelpCenter","openNewsArticle","openChecklists","startChecklist","openNews","openFeatureRequests","isLiveMode"],Gleap.f=function(e){return function(){var t=Array.prototype.slice.call(arguments);window.GleapActions.push({e:e,a:t})}},t=0;t<Gleap.methods.length;t++)Gleap[i=Gleap.methods[t]]=Gleap.f(i);Gleap.load=function(){var t=document.getElementsByTagName("head")[0],i=document.createElement("script");i.type="text/javascript",i.async=!0,i.src="https://sdk.gleap.io/latest/index.js",t.appendChild(i)},Gleap.load(),
    Gleap.initialize("${apiKey}");
	Gleap.showFeedbackButton(false);
    Gleap.setEnvironment("${env}");
}}();`;

	const scriptElement = document.createElement("script");
	scriptElement.type = "text/javascript";
	scriptElement.textContent = script;
	document.head.appendChild(scriptElement);

	// Set up a listener to know when Gleap is fully initialized
	const checkGleapInitialized = setInterval(() => {
		if (window.Gleap && typeof window.Gleap?.identify === "function") {
			clearInterval(checkGleapInitialized);
			window.__gleapInitialized = true;
			processGleapQueue();
		}
	}, 100);

	// Safety timeout after 10 seconds
	setTimeout(() => {
		clearInterval(checkGleapInitialized);
		if (!window.__gleapInitialized) {
			console.warn("Gleap initialization timed out");
			window.__gleapInitialized = true; // Mark as initialized anyway to prevent hanging
			window.Gleap?.disableConsoleLogOverwrite();
			processGleapQueue();
		}
	}, 10000);
}

export function showGleapChat(): void {
	safelyExecuteGleapFunction(() => {
		window.Gleap?.showFeedbackButton(true);
	});
}

export function hideGleapChat(): void {
	safelyExecuteGleapFunction(() => {
		window.Gleap?.showFeedbackButton(false);
	});
}

export function toggleGleapChat(): void {
	safelyExecuteGleapFunction(() => {
		const isOpened = window.Gleap?.isOpened();
		if (isOpened) {
			window.Gleap?.close();
		} else {
			window.Gleap?.open();
		}
	});
}

export function clearGleapIdentity(): void {
	safelyExecuteGleapFunction(() => {
		window.Gleap?.clearIdentity();
	});
}

export function identifyGleapUser(
	userId: string,
	userData: GleapUserData,
): void {
	safelyExecuteGleapFunction(() => {
		window.Gleap?.identify(userId, userData);
	});
}

export function updateGleapContact(contactData: GleapUserData): void {
	safelyExecuteGleapFunction(() => {
		window.Gleap?.updateContact?.(contactData);
	});
}

export function startFeatureRequestForm(): void {
	safelyExecuteGleapFunction(() => {
		window.Gleap?.startClassicForm("featurerequests");
	});
}

export function startBugReportForm(): void {
	safelyExecuteGleapFunction(() => {
		window.Gleap?.startClassicForm("bugreporting", true);
	});
}

export function setGleapSoundEnabled(enabled: boolean): void {
	safelyExecuteGleapFunction(() => {
		if (window.Gleap && typeof window.Gleap.playSound === "function") {
			window.Gleap.playSound(enabled);
		}
	});
}
